import { useState, useMemo, useEffect, memo } from 'react';
import { tw } from 'twind';
import parsePhoneNumber, { formatNumber, getExampleNumber, isValidPhoneNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import CountryCodeSelector from './CountryCodeSelector';
import NationalPhoneNumberInput from './NationalPhoneNumberInput';
import { getCountryDetailsForCode } from './utils';
import { PhoneNumberInputStyleConfig, Options } from './types';
import { pickerStyles } from './styles';
// import { useGeolocationQuery } from 'lib/api';

type PhoneNumberInputProps = {
  value?: string;
  onChange: (phoneNumber: string) => void;
  isValid: (valid: boolean) => void;
  showFlag?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  selectStyleConfig?: any;
  styleConfig?: PhoneNumberInputStyleConfig;
  hasError?: boolean;
  defaultCountryCode?: string;
};

export default memo(function PhoneNumberInput({
  value = '', // full string representation i.e. +12345551234
  onChange,
  isValid,
  styleConfig = pickerStyles,
  selectStyleConfig,
  hasError = false,
  autoFocus = true,
  required = false,
  showFlag = false,
  defaultCountryCode = '+1',
}: PhoneNumberInputProps) {
  const parsed = useMemo(() => parsePhoneNumber(value), [value]);
  const [national, setNational] = useState(parsed?.nationalNumber || '');
  const [country, setCountry] = useState(getCountryDetailsForCode(parsed?.countryCallingCode || parseInt(defaultCountryCode).toString())!);

  useEffect(() => {
    const valid = isValidPhoneNumber(national, country.value);
    isValid(valid);
    onChange(`+${country.countryCode}${national}`);
  }, [national, country, isValid, onChange]);

  const mask = useMemo(() => {
    const phoneNumberExample = getExampleNumber(country.value, examples);
    const nationalNumber = formatNumber(String(phoneNumberExample?.number), 'INTERNATIONAL').substring(country.countryCode!.length + 1);
    const numberGroups = nationalNumber.replace(/\d/g, '0').match(/(\d+)/g);

    const mask = numberGroups?.reduce((finalMask, currentNumberGroup, index) => {
      if (index === 0) {
        return finalMask + `(${currentNumberGroup})`;
      } else {
        return finalMask + `-${currentNumberGroup}`;
      }
    }, '');

    return mask;
  }, [country]);

  const onChangeCountrySelect = (val: Options) => {
    setCountry(val);
  };

  const onChangeInput = (val: string) => {
    setNational(val);
  };

  return (
    <div className={tw`mt-1 flex`}>
      <CountryCodeSelector
        value={country}
        onChange={onChangeCountrySelect}
        showFlag={showFlag}
        hasError={hasError}
        styleConfig={styleConfig}
        selectStyleConfig={selectStyleConfig}
      />
      <NationalPhoneNumberInput
        value={national}
        onChange={onChangeInput}
        mask={mask}
        required={required}
        autoFocus={autoFocus}
        className={styleConfig.inputClass}
      />
    </div>
  );
});
